import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Switch from '@mui/material/Switch';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import { clsx } from 'clsx';
// Components
import Text from '../text';
// Resources
import { stringsRes } from '../../resources/strings';
import { useTheme } from '../../context/ThemeContext';

interface AppearanceMenuItemProps {
    // 'menuItem' for menus built out of MenuItem rows (desktop app bar dropdown, desktop side menu),
    // 'row' for menus built out of plain flex rows (mobile burger menu). Hosts pass their own row classes.
    variant?: 'menuItem' | 'row';
    className?: string;
    iconClassName?: string;
}

const AppearanceMenuItem: FC<AppearanceMenuItemProps> = (props): ReactElement => {
    const { variant = 'menuItem', className, iconClassName } = props;
    const { mode, toggleTheme } = useTheme();
    const classes = useStyles();

    const isDark = mode === 'dark';
    const label = isDark ? stringsRes.appearance.dark : stringsRes.appearance.light;
    const icon = isDark ? <DarkModeOutlined fontSize="small" /> : <LightModeOutlined fontSize="small" />;

    if (variant === 'menuItem') {
        // The menu owns focus for its rows, so the row is the control and the switch only reflects the mode
        return (
            <MenuItem onClick={toggleTheme} aria-label={label} className={clsx(classes.row, className)}>
                <ListItemIcon className={iconClassName}>{icon}</ListItemIcon>
                <Text.ShortMediumText16 text={label} className={classes.label} />
                <Switch checked={isDark} size="small" disableRipple tabIndex={-1} className={classes.readOnlySwitch} />
            </MenuItem>
        );
    }

    // The label makes the whole row activate the switch, so the switch input stays the only focusable element
    return (
        <label className={clsx(classes.row, classes.plainRow, className)}>
            <span className={clsx(classes.plainRowIcon, iconClassName)}>{icon}</span>
            <Text.ShortMediumText16 text={label} />
            <Switch
                checked={isDark}
                onChange={toggleTheme}
                size="small"
                inputProps={{ 'aria-label': label }}
                className={classes.trailingSwitch}
            />
        </label>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
        },
        plainRow: {
            cursor: 'pointer',
            // Align with the drawer links, which take their color from the MuiLink override
            color: theme.palette.primary.main,
        },
        plainRowIcon: {
            display: 'flex',
            marginRight: theme.spacing(2),
        },
        label: {
            color: 'inherit',
        },
        trailingSwitch: {
            marginLeft: 'auto',
        },
        readOnlySwitch: {
            marginLeft: 'auto',
            // Clicks fall through to the row, which owns the toggle
            pointerEvents: 'none',
        },
    }),
);

export default AppearanceMenuItem;
